import { forwardRef, type SelectHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: SelectOption[];
  placeholder?: string;
  invalid?: boolean;
}

/**
 * A native select.
 *
 * Deliberately native rather than a custom widget: it is keyboard-accessible
 * for free, works with every screen reader, and on Android it opens the
 * platform picker, which is far faster to use one-handed than a popup list.
 * The searchable Combobox is used where the list is long.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, options, placeholder, invalid, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        "block h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-slate-900",
        "focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600",
        invalid && "border-blocking ring-1 ring-blocking",
        className,
      )}
      aria-invalid={invalid || undefined}
      {...props}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  ),
);
Select.displayName = "Select";
